import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Controller, useForm } from "react-hook-form";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import clsx from "clsx";
import { useLoader } from "@/app/hooks/useLoader";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { useNavigate } from "react-router";
import { usePaymentOverDueAPI } from "@/app/hooks/api_hooks/dayClosing/usePaymentOverDueAPI";
import { useOverdueReasonsAPI } from "@/app/hooks/api_hooks/dayClosing/useOverdueReasonsAPI";
import ImageUploadComponent from "@/app/components/ui/ImageUploadComponent";
import { useFileToS3LinkGeneratorAPI } from "@/app/hooks/api_hooks/LabReport/useFileToS3LinkGeneratorAPI";
import { FaFilePdf } from "react-icons/fa6";
import UserPetDetailsComponent from "@/app/components/ui/Reusable/UserPetDetailsComponent";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  paymentDetails: any;
  fetchInvoiceList?: () => void;
};

const OverDueModalComponent: React.FC<Props> = ({ open, setOpen, paymentDetails, fetchInvoiceList }) => {
  const { getRouteKey } = useRouter();
  const cancelButtonRef = useRef(null);

  const { refundRequestFilter } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);

  const { execute: overdeuAPI } = usePaymentOverDueAPI();
  const { execute: fetchOverdueReasons, overdueReasons, isLoading: isLoadingReasons } = useOverdueReasonsAPI();
  
  const { execute: uploadToS3 } = useFileToS3LinkGeneratorAPI();
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      refund_type: "wallet",
    },
  });

  const {
    formState: { errors },
  } = useForm();

  const { watch } = formHook;

  useEffect(() => {
    if (refundRequestFilter) {
      formHook.setValue("filters", refundRequestFilter);
    }
  }, [refundRequestFilter]);

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // ✅ read query params
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const isLoadingCashCollect = useLoader("isCollectAmountDataInProgress");

  const invoiceId = paymentDetails?.billing_details
    ? paymentDetails?.invoice_id
    : paymentDetails?.invoice_details?.invoice_id;

  // Reasons are invoice specific, so refetch whenever the modal opens for an invoice
  useEffect(() => {
    if (open && invoiceId) {
      formHook.setValue("reason", null);
      formHook.setValue("subReason", null);
      fetchOverdueReasons(invoiceId);
    }
  }, [open, invoiceId]);

  const reasonItems = useMemo(() => overdueReasons.map((r) => ({ title: r.reason, id: r.reason })), [overdueReasons]);

  const handelClick = () => {
    const constructedBody = {
      clinic_id: mappedClinicId,
      invoice_id: invoiceId,
      invoice_status: paymentDetails?.billing_details
        ? paymentDetails?.status
        : paymentDetails?.invoice_details?.invoice_status,
      overdue_reason: formHook.watch("reason"),
      overdue_sub_reason: formHook.watch("subReason") || null,
      overdue_rca: formHook.watch("otherReason"),
      overdue_attachment_urls: formHook.watch("receipt_url") ? [formHook.watch("receipt_url")] : [],
    };
    overdeuAPI(constructedBody).then(({ status }) => {
      if (status) {
        setOpen(false);
        processId &&
          navigate(
            `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
          );
        !processId && fetchInvoiceList();
      }
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingFiles(true); // start loader
    try {
      const result = await uploadToS3(file, "CASH", "FileList");
      if (result.status) {
        formHook.setValue("receipt_url", result.message);
      } else {
        // toast.error(`Failed to upload ${file.name}`);
      }
    } catch (error) {
      console.error("File upload error:", error);
    } finally {
      setUploadingFiles(false); // stop loader
    }
  };

  const reason = formHook.watch("reason");
  const subReason = formHook.watch("subReason");
  const subReasonItems = useMemo(
    () =>
      (overdueReasons.find((r) => r.reason === reason)?.sub_reasons ?? []).map((sr) => ({
        title: sr,
        id: sr,
      })),
    [overdueReasons, reason],
  );
  const otherReason = formHook.watch("otherReason") || "";
  const receiptUrl = formHook.watch("receipt_url");

  // Receipt image is mandatory only for this specific overdue reason
  const isReceiptRequired = reason === "Payment Deducted from Client but Failed in POS/VMS";

  const isInvalid =
    !reason ||
    (subReasonItems.length > 0 && !subReason) ||
    otherReason.trim().length < 25 ||
    (isReceiptRequired && !receiptUrl); // < 25 is invalid

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-xl">
                <div className="bg-white p-6">
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-red-500 font-semibold text-base">Raise Overdue</p>

                    <span
                      onClick={() => {
                        setOpen(false);
                      }}
                    >
                      <XMarkIcon className="h-5 w-5 cursor-pointer" />
                    </span>
                  </div>
                  <UserPetDetailsComponent
                    userDetails={paymentDetails?.user_details}
                    petDetails={
                      paymentDetails?.pet_details?.length > 0
                        ? paymentDetails?.pet_details[0]
                        : paymentDetails?.pet_wise_items?.length > 0 && paymentDetails?.pet_wise_items[0]?.pet_details
                    }
                  />
                  <div className=" items-start py-3   w-full ">
                    <div className="flex flex-col   gap-4 ">
                      <SearchableSelectMenu
                        errorMessages={[{ message: "", type: "required" }]}
                        onSelectItem={(item) => {
                          if (item) {
                            formHook.setValue("reason", item.title);
                            formHook.setValue("subReason", null);
                          }
                        }}
                        fieldError={formHook.formState.errors.reason}
                        register={formHook.register("reason", { required: true })}
                        selectItems={reasonItems}
                        placeholder={isLoadingReasons ? "Loading reasons..." : "Choose Overdue Reason"}
                        showTooltips={true}
                        showTypedErrors={false}
                        showDropdownIcon={true}
                        defaultSelected={reasonItems.find((oc) => oc.title === reason)}
                        listBoxClassName="w-full"
                        className="text-gray-800 w-10 z-50"
                        containerClassName="w-full"
                      />
                      {subReasonItems.length > 0 && (
                        <SearchableSelectMenu
                          errorMessages={[{ message: "", type: "required" }]}
                          onSelectItem={(item) => {
                            if (item) {
                              formHook.setValue("subReason", item.title);
                            }
                          }}
                          fieldError={formHook.formState.errors.subReason}
                          register={formHook.register("subReason")}
                          selectItems={subReasonItems}
                          placeholder="Choose Sub Reason"
                          showTooltips={true}
                          showTypedErrors={false}
                          showDropdownIcon={true}
                          defaultSelected={subReasonItems.find((sr) => sr.title === subReason)}
                          listBoxClassName="w-full"
                          className="text-gray-800 w-10 z-40"
                          containerClassName="w-full"
                        />
                      )}
                      {errors.billingStatus && (
                        <p className="text-red-500 text-xs mt-1">{errors.billingStatus.message as any}</p>
                      )}

                      <Controller
                        name="otherReason"
                        control={formHook.control} // Ensure you're using the correct form instance
                        rules={{
                          required: "share the reason",
                          validate: (value) => value.trim() !== "" || "Reason cannot be blank.",
                          minLength: {
                            value: 25,
                            message: "Minimum 25 characters required",
                          },
                        }}
                        render={({ field }) => (
                          <textarea
                            {...field}
                            rows={4}
                            placeholder="share the reason"
                            className={`w-full p-2 border ${formHook.formState.errors.otherReason ? "border-red-400" : "border-gray-300"} rounded-md text-sm`}
                          />
                        )}
                      />

                      {formHook.formState.errors.otherReason && (
                        <p className="text-red-500 text-[10px] -mt-2">
                          {formHook.formState.errors.otherReason.message as any}
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <div className="mt-1 flex items-center justify-start mb-3">
                        {uploadingFiles ? (
                          <div className="flex items-center justify-center h-32 w-32 border rounded-lg">
                            <svg
                              className="animate-spin h-6 w-6 text-gray-600"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              ></path>
                            </svg>
                          </div>
                        ) : watch("receipt_url") ? (
                          <div className="mt-3 relative inline-block">
                            {watch("receipt_url").toLowerCase().endsWith(".pdf") ? (
                              // 📄 PDF Preview
                              <div className="flex flex-col items-center justify-center bg-gray-100 rounded-lg border p-4 w-32 h-32">
                                <FaFilePdf className="text-red-600 text-5xl" />
                              </div>
                            ) : (
                              // 🖼️ Image Preview
                              <img
                                src={watch("receipt_url")}
                                alt="Receipt Preview"
                                className="max-h-32 rounded-lg border"
                              />
                            )}

                            <button
                              type="button"
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-opacity-80"
                              onClick={() => formHook.setValue("receipt_url", null)}
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <ImageUploadComponent
                            handleFileChange={handleFileUpload}
                            heading="Click to Upload Receipt"
                            text="or drag and drop PDF,JPEG,PNG up to 5MB"
                            data={watch("receipt_url")}
                            required={isReceiptRequired}
                          />
                        )}
                      </div>
                      {isReceiptRequired && !receiptUrl && (
                        <p className="text-red-500 text-[10px] -mt-2 mb-3">
                          Receipt upload is mandatory for this reason.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className=" gap-4 w-full ">
                    <button
                      disabled={isInvalid}
                      className={clsx(
                        "w-full py-2 text-sm font-medium rounded-md flex items-center justify-center gap-2",
                        {
                          "bg-[#939393] text-white": isInvalid, // inactive
                          "bg-black text-white": !isInvalid, // active
                        },
                      )}
                      onClick={() => handelClick()}
                    >
                      {isLoadingCashCollect ? <CircleSpinnerLoader h="20" w="20" /> : "Submit Reason"}
                    </button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default OverDueModalComponent;
